/**
 * Histórico dos desligados. Fase 6 (docs/00F §3), renomeado em 26/jul —
 * inclui archived e suspended (não existe mais estágio de "pausado" no
 * kanban de Gestão; quem não está em evolução cai aqui, sem perder nada:
 * o status muda, mas posts, briefing, métricas e timeline continuam
 * intactos e acessíveis pelo perfil do cliente).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { ClientDTO } from "@unik/shared";
import { api } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { Card, EmptyState, Skeleton } from "../../ui/Card";
import { TIER_LABEL } from "../../lib/labels";

export function Desativados() {
  const [clients, setClients] = useState<ClientDTO[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    const all = await api.get<ClientDTO[]>("/clients").catch(() => []);
    setClients(all.filter((c) => c.status === "archived" || c.status === "suspended"));
  }
  useEffect(() => {
    void load();
  }, []);

  async function reativar(id: string) {
    setBusyId(id);
    try {
      await api.patch(`/clients/${id}/status`, { status: "active" });
      await load();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <AdminShell>
      <Link to="/gestao" className="type-caption text-text-tertiary">← Voltar à Gestão</Link>
      <h1 className="type-h1 mt-1 text-text-cream">Histórico dos desligados</h1>
      <p className="type-body-sm mt-1 text-text-secondary">
        Nada é apagado quando um cliente é desligado — o histórico completo (posts, briefing,
        métricas) continua no perfil dele. Reative quando quiser retomar o trabalho.
      </p>

      {clients === null && <Skeleton className="mt-6 h-24" />}

      {clients?.length === 0 && (
        <div className="mt-8">
          <EmptyState title="Nenhum cliente desligado" description="Quando você desligar um cliente, ele aparece aqui." />
        </div>
      )}

      {clients !== null && clients.length > 0 && (
        <div className="mt-6 flex flex-col gap-3">
          {clients.map((c) => (
            <Card key={c.id}>
              <div className="flex items-center justify-between gap-3">
                <Link to={`/clientes/${c.id}`} className="min-w-0">
                  <h2 className="type-h3 truncate text-text-cream">{c.name}</h2>
                  <p className="type-caption mt-1 text-text-tertiary">
                    <span className="text-gold">{TIER_LABEL[c.tier]}</span> · desligado em{" "}
                    {new Date(c.updatedAt).toLocaleDateString("pt-BR")}
                  </p>
                </Link>
                <Button size="sm" variant="secondary" loading={busyId === c.id} onClick={() => reativar(c.id)}>
                  Reativar
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
